import {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import nth from 'lodash/nth';

import {useBreakPoint, useDestructiveDialog, useFeatureToggle, useIntents, useModal, useQueryParams} from '@hooks';

import {
  type MeetingRoom,
  getBuildingByCheckInOrWorkspaceReservation,
  getFloorsWithMapByBuildingId,
  getUser,
  loadAllMeetingRooms,
  withAsyncThunkErrorHandling,
  getFloorById,
  getShouldShowHereAndNowConnectYourCalendarFullScreen,
  getDisplayCalendarSyncTile,
  type Area,
  getIsLoadingCalendarState,
  getIsLoadingUserCheckIn,
  getIsLoadingWorkspaceReservations,
  getIsOccupancyEnabled,
  loadWorkspaceOccupancy,
  getWorkspaceOccupancyLoadingStatusForBuildingId,
  getAllMeetingRoomSuggestions,
  getAppLanguage,
  getFloorsByBuildingId,
  getAvailableDeskAmenityDataByBuildingId,
  getAvailableActivityTypeDataByBuildingId,
  getAvailableDeskAmenitiesForBuilding,
  getAvailableActivityTypesForBuilding,
  getAreasWithSensorData,
  AreaWithSensorInfo,
  getDefaultBuilding,
  getAllBuildings,
  getBuildingById,
  getHasMeetingPreconditions,
  getAllMeetingRoomSuggestionsUnfiltered,
  getMapDataByFloorId,
  useAppDispatch,
  useAppSelector,
  getMeetingRoomSuggestionByBuildingId,
  getIsHMEnabledAndIntegrationEnabled,
  getIsThreeDMapsEnabled,
} from '@lib/store';

import {
  addMinutes,
  parseISO,
  startOfDay,
  startOfMinute,
  differenceInMinutes,
  startOfToday,
  format,
  endOfToday,
} from 'date-fns';

import {DEFAULT_NEW_EVENT_DURATION as DURATION, MINUTES_TRESHOLD_FOR_STALE_DATA} from '@constants';
import {Div, FlexCol, FlexRow, H3} from '@quarks';
import {
  HaNControls,
  HaNMaps,
  HaNModalContainer,
  HaNFilters,
  HaNHorizontalListContent,
  HaNVerticalListContent,
  isMeetingRoom,
  HaNCalendarSyncTile,
  HaNCalendarSync,
  SingleSelectCard,
  HaNExploreTab,
  HaNExploreSearchBar,
} from '@organisms';
import {Canvas, Heading, HereAndNowLayout} from '@templates';

import {AreaEventTarget, RoomEventTarget} from '../../../../../../submodules/map/mapiq-map/EventTarget';
import {
  ControlsRow,
  HaNPageHeader,
  MapWrapper,
  RoomListGridArea,
  StyledIconNav,
  StyledNavLink,
  StyledNavLinkWithIcon,
  StyledTabs,
  Title,
} from './styles';
import {IconButton, NoMapPlaceholder, SwiperRefType} from '@molecules';
import {useNavigate} from 'react-router-dom';
import {Loader} from '@atoms';
import {
  constructHereAndNowRoomsFilter,
  constructHereAndNowWorkspacesFilter,
  QueryParamConstants,
  useFilterData,
  useMeetingRoomAmenities,
} from '@lib/logic';
import {getDeskAmenityIcon, getRoomEquipementIcon} from '@utils';
import {useExploreState} from './useExploreState';
import {FacilityResource} from '@lib/store/src/data/buildingResources';
import {IntentType, trackEvent, trackPageComponentedRendered} from '@lib/infrastructure';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {FloorSelector} from './FloorSelector/FloorSelector';

const initialStartDateTime = startOfMinute(new Date()).toISOString();
const initialEndDateTime = addMinutes(parseISO(initialStartDateTime), 30).toISOString();

export const HaN_Previous_Tab = 'HaN_Previous_Tab';

export const HereAndNow = (props: {tab: string}) => {
  const enabledFeatures = useFeatureToggle();
  const queryParamKeys = QueryParamConstants.hereAndNow.explore;
  // * UI
  const ref = useRef<SwiperRefType | null>(null);
  const {t} = useTranslation();
  const breakPoint = useBreakPoint();
  const {addPages, openModal} = useModal();
  const {destructiveDialog} = useDestructiveDialog();
  const tab = props.tab;

  // * URL INITIALIZATION
  const {
    buildingId: linkedBuildingId,
    q: exploreQuery,
    r,
    removeURLParams,
    i: intentRaw,
  } = useQueryParams([
    queryParamKeys.search,
    queryParamKeys.buildingId,
    queryParamKeys.resourceId,
    QueryParamConstants.intent,
  ]);
  const [preselectedObjectId] = useState(r);
  const {setIntentIfNotSet} = useIntents();

  // react router tells us to not perform navigation in a render function
  // so this needs to be in an effect
  useEffect(() => {
    setIntentIfNotSet(IntentType.parse(intentRaw));
    removeURLParams();
  }, [setIntentIfNotSet, intentRaw, removeURLParams]);

  // * FILTERS
  const [{startDateTime, endDateTime}, setRoomDateTimes] = useState({
    startDateTime: initialStartDateTime,
    endDateTime: initialEndDateTime,
  });

  const lastOccupancyLoadForBuildingId = useRef<Record<string, Date | undefined>>({});

  // * STATE
  const [roomInformationOpen, setRoomInformationOpen] = useState(false);
  const [warnBeforeOpeningNewRoom, setWarnBeforeOpeningNewRoom] = useState(false);
  const [hoverRoomId, setHoverRoomId] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<MeetingRoom | AreaWithSensorInfo | FacilityResource | null>(
    null,
  );
  const [selectedObjectForMap, setSelectedObjectForMap] = useState<
    MeetingRoom | AreaWithSensorInfo | FacilityResource | null
  >(null);

  // * REDUX SELECTORS
  const dispatch = useAppDispatch();
  const building = useAppSelector((state) =>
    getBuildingByCheckInOrWorkspaceReservation(state, startOfDay(new Date()).toISOString()),
  );
  const allBuildings = useAppSelector(getAllBuildings);
  const allBuildingIds = allBuildings.map((b) => b.id);
  const {id: loggedInUserId} = useAppSelector(getUser);
  const sessionBuildingIdKey = useMemo(() => `${loggedInUserId}_buildingId`, [loggedInUserId]);
  const sessionBuildingId = linkedBuildingId ?? sessionStorage.getItem(sessionBuildingIdKey) ?? '';
  const {id: reservationBuildingId = ''} = building ?? {};
  const initBuildingId =
    allBuildingIds.length && allBuildingIds.includes(sessionBuildingId) ? sessionBuildingId : reservationBuildingId;
  const navigate = useNavigate();

  const [buildingId, setBuildingId] = useState(initBuildingId);
  const minutesThresholdForStaleData = 5;
  const showCalendarConsentMessageNoOccupancy = useAppSelector(getShouldShowHereAndNowConnectYourCalendarFullScreen);
  const displayCalendarSyncTile = useAppSelector(getDisplayCalendarSyncTile);
  const {WorkspacesOnHereAndNow: isWorkspacesOnHereAndNowEnabled} = useFeatureToggle();
  const isMeetingCalendarEnabled = useAppSelector(getIsHMEnabledAndIntegrationEnabled);
  const calendarLoadingStatus = useAppSelector(getIsLoadingCalendarState);
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const hasMeetingPreconditions = useAppSelector(getHasMeetingPreconditions);
  const floors = useAppSelector((state) => getFloorsByBuildingId(state, buildingId));
  const appLanguage = useAppSelector(getAppLanguage);
  const loadingCalendar = calendarLoadingStatus === 'Loading';
  const occupancyLoadingStatus = useAppSelector((state) =>
    getWorkspaceOccupancyLoadingStatusForBuildingId(state, buildingId),
  );
  const isLoadingSensors = occupancyLoadingStatus === 'loading' && isOccupancyEnabled;
  const isThreeDMapsEnabled = useAppSelector(getIsThreeDMapsEnabled);
  const {status: meetingRoomLoadingStatus} =
    useAppSelector((state) => getMeetingRoomSuggestionByBuildingId(state, buildingId)) ?? {};
  const isLoadingUserCheckIn = useAppSelector(getIsLoadingUserCheckIn);
  const isLoadingWorkspaceReservations = useAppSelector(getIsLoadingWorkspaceReservations);
  const isLoadingBuilding = isLoadingUserCheckIn || isLoadingWorkspaceReservations;
  const loadingMeetingRooms = meetingRoomLoadingStatus === 'loading';
  const isLoading = loadingCalendar || loadingMeetingRooms || isLoadingSensors;
  const areasWithSensorData = useAppSelector((state) => getAreasWithSensorData(state, buildingId, t));
  const allRooms = useAppSelector((state) => getAllMeetingRoomSuggestions(state, buildingId));
  const {availableDeskAmenities, status: deskAmenitiesLoadingStatus} = useAppSelector(
    getAvailableDeskAmenityDataByBuildingId(buildingId),
  );
  const allRoomsWithAnyScore = useAppSelector((state) => getAllMeetingRoomSuggestionsUnfiltered(state, buildingId));
  const {availableActivityTypes, status: activityTypesLoadingStatus} = useAppSelector(
    getAvailableActivityTypeDataByBuildingId(buildingId),
  );
  const defaultBuilding = useAppSelector(getDefaultBuilding);

  const buildingName = useAppSelector((state) => getBuildingById(state, buildingId))?.name || t('translation:Booked');

  // FILTERS
  const floorsWithRooms = useMemo(() => {
    const relevantFloorIds = new Set(allRooms.map((r) => r.floorId));
    return floors.filter((f) => relevantFloorIds.has(f.id));
  }, [floors, allRooms]);

  const floorsWithWorkspaces = floors.filter((floor) =>
    areasWithSensorData.map((area) => area.floorId).includes(floor.id),
  );
  const {meetingRoomAmenitiesByBuilding} = useMeetingRoomAmenities();
  const meetingRoomAmenitiesForBuilding = meetingRoomAmenitiesByBuilding[buildingId];

  const roomFilters = useMemo(
    () =>
      constructHereAndNowRoomsFilter(
        enabledFeatures.ImprovedHaN ? [] : floorsWithRooms,
        hasMeetingPreconditions,
        t,
        appLanguage,
        meetingRoomAmenitiesForBuilding?.activities ?? [],
        meetingRoomAmenitiesForBuilding?.equipment ?? [],
        getRoomEquipementIcon,
      ),
    [
      enabledFeatures.ImprovedHaN,
      appLanguage,
      floorsWithRooms,
      hasMeetingPreconditions,
      meetingRoomAmenitiesForBuilding?.activities,
      meetingRoomAmenitiesForBuilding?.equipment,
      t,
    ],
  );

  const areaFilters = useMemo(
    () =>
      constructHereAndNowWorkspacesFilter(
        enabledFeatures.ImprovedHaN ? [] : floorsWithWorkspaces,
        availableDeskAmenities,
        availableActivityTypes,
        getDeskAmenityIcon,
        isOccupancyEnabled,
        t,
      ),
    [
      availableActivityTypes,
      availableDeskAmenities,
      enabledFeatures.ImprovedHaN,
      floorsWithWorkspaces,
      isOccupancyEnabled,
      t,
    ],
  );
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(floors[0]?.id || null);

  // Current set-up forces us to do all these things in the parent component...
  const exploreState = useExploreState({
    buildingId,
    initialQuery: exploreQuery ?? '',
    preselectedResourceId: preselectedObjectId,
    selectedFloorId,
    setSelectedFloorId,
  });

  useEffect(
    function loadAvailability() {
      dispatch(
        actions.fetchWorkspaceReservationCountsForWorkday({
          workday: {
            buildingId,
            localStart: format(startOfToday(), "yyyy-MM-dd'T'HH:mm:ss"),
            localEnd: format(endOfToday(), "yyyy-MM-dd'T'HH:mm:ss"),
          },
        }),
      );
    },
    [dispatch, buildingId],
  );

  const allRoomsForFloor = useMemo(() => {
    return allRooms.filter((room) => room.floorId === selectedFloorId);
  }, [allRooms, selectedFloorId]);

  const allAreasForFloor = useMemo(() => {
    return areasWithSensorData.filter((area) => area.floorId === selectedFloorId);
  }, [areasWithSensorData, selectedFloorId]);

  const {filteredData: rooms, selectedFilters: roomSelectedFilters} = useFilterData(
    'HereAndNow_Rooms',
    buildingId,
    enabledFeatures.ImprovedHaN ? allRoomsForFloor : allRooms,
    roomFilters,
    'and',
    loadingMeetingRooms,
  );

  const {filteredData: areas} = useFilterData(
    'HereAndNow_Workspaces',
    buildingId,
    enabledFeatures.ImprovedHaN ? allAreasForFloor : areasWithSensorData,
    areaFilters,
    'and',
    isLoadingSensors,
  );

  const mapFloorId =
    tab === 'explore'
      ? exploreState.floorId
      : selectedObjectForMap?.floorId && selectedObjectForMap.buildingId === buildingId
      ? selectedObjectForMap.floorId
      : tab === 'rooms'
      ? enabledFeatures.ImprovedHaN
        ? selectedFloorId
        : rooms[0]?.floorId || null
      : enabledFeatures.ImprovedHaN
      ? selectedFloorId
      : areas[0]?.floorId || null;

  const mapData = useAppSelector(getMapDataByFloorId(buildingId, mapFloorId || ''));
  const floorsWithMap = useAppSelector((state) => getFloorsWithMapByBuildingId(state, buildingId));
  const mapFloor = useAppSelector((state) => getFloorById(state, mapFloorId || ''));

  const [roomListVerticalOpen, setRoomListVerticalOpen] = useState(floorsWithMap.length ? false : true);
  // * CONSTS

  const duration = Number(roomSelectedFilters['SECTION_ID_DURATION']?.[0]) || DURATION;
  const displayControlsRow = mapFloor && !showCalendarConsentMessageNoOccupancy;

  const paddingLeftPixels = breakPoint === 'xLarge' ? 420 : 0; // Width of a modal
  const paddingBottomPixels = breakPoint === 'small' ? 100 : 0; // Height of a horizontal rooms list
  const areFiltersTransparent = breakPoint === 'small' && roomListVerticalOpen;

  const viewportRestrictions = useMemo(
    () => ({
      paddingTopPixels: 80, // Filters height and padding
      paddingRightPixels: 0,
      paddingBottomPixels: paddingBottomPixels,
      paddingLeftPixels,
    }),
    [paddingLeftPixels, paddingBottomPixels],
  );

  const objectToSelectInitially = useMemo(
    () => (tab === 'workspaces' ? areas[0] ?? null : tab === 'rooms' ? rooms[0] ?? null : null),
    [tab, areas, rooms],
  );

  const isInMobileView = breakPoint === 'small';
  const hasInitiallySelectedObject = selectedObject !== null;

  // * FUNCS

  const loadHereAndNowRooms = useCallback(() => {
    const loadRooms = async () => {
      await dispatch(
        withAsyncThunkErrorHandling(() =>
          loadAllMeetingRooms({
            startDateTime: newStartDateTime,
            endDateTime: newEndDateTime,
            buildingId: buildingId,
            internalAttendeeIds: [loggedInUserId],
            totalAttendees: 1,
          }),
        ),
      );
    };

    // Check required feature flags
    if (!(isOccupancyEnabled || isMeetingCalendarEnabled)) return;

    const newStartDateTime = startOfMinute(new Date()).toISOString();
    const newEndDateTime = addMinutes(parseISO(newStartDateTime), duration).toISOString();

    setRoomDateTimes({startDateTime: newStartDateTime, endDateTime: newEndDateTime});

    if (buildingId && duration && loggedInUserId) {
      loadRooms();
    }
  }, [buildingId, dispatch, duration, loggedInUserId, isOccupancyEnabled, isMeetingCalendarEnabled]);

  const fetchOccupancySensorData = useCallback(() => {
    if (!isLoadingBuilding && buildingId) {
      dispatch(withAsyncThunkErrorHandling(() => loadWorkspaceOccupancy(buildingId)));

      lastOccupancyLoadForBuildingId.current[buildingId] = startOfMinute(new Date());
    }
  }, [buildingId, dispatch, isLoadingBuilding]);

  const refreshRoomsData = () => {
    if (buildingId && loggedInUserId) {
      const now = startOfMinute(new Date());
      if (differenceInMinutes(now, parseISO(startDateTime)) >= MINUTES_TRESHOLD_FOR_STALE_DATA) {
        loadHereAndNowRooms();
      }
    }
  };

  const toggleListOrMapView = () => {
    if (!roomListVerticalOpen) {
      ref.current?.scrollToSlide(0);
    }
    setRoomListVerticalOpen(!roomListVerticalOpen);
  };

  const openObjectInformation = async (entity: MeetingRoom | AreaWithSensorInfo | FacilityResource | undefined) => {
    // Some of the areas have capacity of 0 which do not get fetched
    // because of that they will not be found and we need not handle possible undefined value
    if (!entity) {
      setSelectedObjectForMap(null);
      setSelectedObject(null);
      return;
    }

    if (isMeetingRoom(entity) && warnBeforeOpeningNewRoom) {
      const confirmation = await destructiveDialog(
        t('meeting:DiscardEventChangesTitle'),
        t('meeting:DiscardEventChangesMessage'),
        'warning',
        t('meeting:DiscardEventChangesConfirm'),
      );

      if (!confirmation) return;
    }

    setSelectedObjectForMap(entity);
    setSelectedObject(entity);
    setRoomInformationOpen(true);
  };

  const closeDetailsModal = () => {
    setRoomInformationOpen(false);
    setSelectedObject(null);
    if (exploreState.selectedResource) {
      exploreState.setSelectedResource(null);
    }
  };

  const handleOnBackExplore = () => {
    if (exploreState.rawQuery.length) {
      exploreState.setRawQuery('');
      return;
    }

    const previousTab = sessionStorage.getItem(HaN_Previous_Tab) ?? '/now/rooms';

    setRoomInformationOpen(false);
    setSelectedObject(null);
    setSelectedObjectForMap(null);

    navigate(previousTab);
  };

  const resetListState = () => {
    if (breakPoint === 'small') {
      ref.current?.scrollToSlide(0);
    }
  };

  const resetEntityState = useCallback(
    (tabValue?: string) => {
      if (breakPoint === 'small') {
        const objectToSelect = tabValue === 'rooms' ? rooms[0] : areas[0];
        setRoomInformationOpen(false);
        setSelectedObject(objectToSelect);
        setSelectedObjectForMap(objectToSelect);
      } else {
        setRoomInformationOpen(false);
        setSelectedObject(null);
        setSelectedObjectForMap(null);
      }
    },
    [areas, breakPoint, rooms],
  );

  const handleRoomClick = (e: RoomEventTarget | AreaEventTarget) => {
    const isSmall = breakPoint === 'small';

    if (e.type === 'room' || e.roomId) {
      const roomCollection = isThreeDMapsEnabled ? allRoomsWithAnyScore : rooms;
      const roomIndex = roomCollection.findIndex((room) => room.id === e.roomId);
      const room = roomCollection[roomIndex];

      if (isSmall) {
        ref?.current?.scrollToSlide(roomIndex);
        return;
      }

      if (room) {
        openObjectInformation(room);
      }

      return;
    }

    if (e.areaId) {
      const areaIndex = areas.findIndex((area) => area.id === e.areaId);
      const area = areas[areaIndex];

      if (isSmall) {
        ref?.current?.scrollToSlide(areaIndex);
        return;
      }

      if (area) {
        openObjectInformation(area);
      }
    }
  };

  const handleOnSlideChange = (visibleObject: MeetingRoom | AreaWithSensorInfo) => {
    setSelectedObject(visibleObject);
    setSelectedObjectForMap(visibleObject);
  };

  const handleBookNow = () => {
    if (selectedObject !== null) {
      if (isMeetingRoom(selectedObject) && breakPoint === 'small') {
        const currentIndex = rooms.indexOf(selectedObject!);
        const nextRoomValue = nth(rooms, currentIndex + 1) ?? nth(rooms, currentIndex - 1) ?? null;
        setSelectedObjectForMap(nextRoomValue);
        setSelectedObject(nextRoomValue);
      }
    }
  };

  const handleBuildingSelectClick = () => {
    addPages([
      <SingleSelectCard
        defaultId={defaultBuilding?.id || buildingId}
        preselectedId={buildingId}
        headerTitle={t('screen:MeetingBuildingSelection')}
        entities={allBuildings.reduce(
          (buildings, currentBuilding) => {
            if (currentBuilding.id !== defaultBuilding?.id) {
              buildings.push({
                id: currentBuilding?.id ?? '',
                label: currentBuilding?.name ?? '',
              });
            }
            return buildings;
          },
          [{id: defaultBuilding?.id || '', label: defaultBuilding?.name || ''}],
        )}
        onSelect={(selectedBuildingId: string) => {
          trackEvent('HereAndNow__ChangeBuilding', {id: selectedBuildingId});
          setBuildingId(selectedBuildingId);
          closeDetailsModal();
        }}
      />,
    ]);
    openModal();
  };

  // * SIDEEFFECTS

  useEffect(() => {
    switch (tab) {
      case 'rooms':
        trackPageComponentedRendered('hereAndNowRooms', !isLoading);
        break;
      case 'workspaces':
        trackPageComponentedRendered('hereAndNowWorkspaces', !isLoading);
        break;
      case 'explore':
        trackPageComponentedRendered('hereAndNowExplore', !isLoading);
        break;
    }
  }, [dispatch, isLoading, tab]);

  useEffect(() => {
    if (tab === 'explore') {
      if (exploreState.selectedResource?.type === 'room') {
        const selectedRoom = allRoomsWithAnyScore.find((room) => room.id === exploreState.selectedResource?.id);
        if (selectedRoom) {
          openObjectInformation(selectedRoom);
        }
      } else if (exploreState.selectedResource?.type === 'area') {
        const selectedWorkSpace = areas.find((area) => area.id === exploreState.selectedResource?.id);
        if (selectedWorkSpace) {
          openObjectInformation(selectedWorkSpace);
        }
      } else if (exploreState.selectedResource?.type === 'facility') {
        openObjectInformation(exploreState.selectedResource);
      } else if (!exploreState.selectedResource && selectedObject) {
        openObjectInformation(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    exploreState.selectedResource?.id,
    exploreState.selectedResource?.type,
    rooms,
    exploreState.selectedResource,
    selectedObject,
    areas,
  ]);

  useEffect(() => {
    // For the building selector we want to preserve the selection
    // This particular implementation with session storage is a one off solution.
    // We are checking for the string not to be empty since default redux selector value is ''
    if (!buildingId && reservationBuildingId) {
      setBuildingId(reservationBuildingId);
    }
    sessionStorage.setItem(sessionBuildingIdKey, buildingId);
  }, [buildingId, reservationBuildingId, sessionBuildingIdKey]);

  useEffect(() => {
    if (isLoadingBuilding || !buildingId) return;

    if (deskAmenitiesLoadingStatus === 'NotLoaded') {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableDeskAmenitiesForBuilding({buildingId})));
    }

    if (activityTypesLoadingStatus === 'NotLoaded') {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableActivityTypesForBuilding({buildingId})));
    }
  }, [activityTypesLoadingStatus, buildingId, deskAmenitiesLoadingStatus, dispatch, isLoadingBuilding]);

  useEffect(() => {
    const correctTab = tab === 'workspaces' || tab === 'rooms';
    function matchObjecAndTabType(
      objectType: MeetingRoom['type'] | Area['type'] | FacilityResource['type'],
      tabType: 'workspaces' | 'rooms',
    ) {
      if (
        (objectType === 'area' && tabType === 'workspaces') ||
        (objectType === 'meetingRoom' && tabType === 'rooms')
      ) {
        return true;
      }
      return false;
    }
    if (correctTab && selectedObject && !matchObjecAndTabType(selectedObject.type, tab)) resetEntityState(tab);
  }, [resetEntityState, selectedObject, tab]);

  useEffect(() => {
    // We are currently fetching sensor data even when on rooms tab,
    // looking to optimise this in the future with reduxQuery.

    if (!isOccupancyEnabled || !isWorkspacesOnHereAndNowEnabled) return;

    const now = startOfMinute(new Date());
    const lastLoadForBuilding = lastOccupancyLoadForBuildingId.current[buildingId];

    if (!lastLoadForBuilding || differenceInMinutes(now, lastLoadForBuilding) >= minutesThresholdForStaleData) {
      fetchOccupancySensorData();
    }
  }, [buildingId, isOccupancyEnabled, fetchOccupancySensorData, isWorkspacesOnHereAndNowEnabled]);

  useEffect(() => {
    setRoomListVerticalOpen(floorsWithMap.length ? false : true);
  }, [floorsWithMap]);

  useEffect(() => loadHereAndNowRooms(), [loadHereAndNowRooms]);

  useEffect(() => {
    if (isInMobileView && !hasInitiallySelectedObject) {
      // On mobile we need to pre-select a room, but not on larger screens
      setSelectedObject(objectToSelectInitially);
      setSelectedObjectForMap(objectToSelectInitially);
    }
  }, [hasInitiallySelectedObject, isInMobileView, objectToSelectInitially]);

  // * UI

  if (isLoading) {
    return (
      <Canvas centerContent>
        <Loader />
      </Canvas>
    );
  }

  if (allBuildingIds.length && !allBuildingIds.includes(sessionBuildingId)) {
    sessionStorage.removeItem(sessionBuildingIdKey);
  }

  return (
    <>
      <Heading>
        <HaNPageHeader
          buttonIcons={
            floorsWithMap.length
              ? [
                  {
                    icon: roomListVerticalOpen ? 'map' : 'weekView',
                    label: roomListVerticalOpen ? t('hereAndNow:Map') : t('hereAndNow:ListOfRooms'),
                    onClick: toggleListOrMapView,
                    showButton: 'mobile',
                  },
                ]
              : undefined
          }
          title={buildingName}
          onBuildingSelect={allBuildings.length > 1 ? handleBuildingSelectClick : undefined}
        />
      </Heading>
      <HereAndNowLayout
        $displayCalendarSyncTile={displayCalendarSyncTile}
        $areWorkspacesEnabled>
        {mapFloorId && mapData.status !== 'Failed' ? (
          <MapWrapper>
            <HaNMaps
              buildingId={buildingId}
              floorId={mapFloorId}
              hoverObjectId={hoverRoomId}
              onObjectClick={handleRoomClick}
              onAreaOrRoomHover={setHoverRoomId}
              rooms={isThreeDMapsEnabled ? allRoomsWithAnyScore : rooms}
              workspaces={areas}
              selectedObject={selectedObject}
              viewportRestrictions={viewportRestrictions}
              exploreState={exploreState}
              onOutsideClick={closeDetailsModal}
            />
          </MapWrapper>
        ) : (
          <MapWrapper>
            <NoMapPlaceholder />
          </MapWrapper>
        )}
        {displayCalendarSyncTile && (
          <Div
            display="none"
            sm={{display: 'grid', gridArea: 'calendar-sync-tile', zIndex: 1}}>
            <HaNCalendarSyncTile />
          </Div>
        )}
        {tab !== 'explore' && (
          <Div
            gridArea="tabs"
            display="none"
            zIndex={10}
            sm={{
              display: roomListVerticalOpen ? 'none' : 'block',
            }}>
            <StyledTabs>
              {(isMeetingCalendarEnabled || isOccupancyEnabled) && (
                <StyledNavLink
                  onClick={() => {
                    resetEntityState('rooms');
                    refreshRoomsData();
                    sessionStorage.setItem(HaN_Previous_Tab, '/now/rooms');
                  }}
                  $isActive={tab === 'rooms'}
                  to={'/now/rooms'}>
                  {t('hereAndNow:Rooms')}
                </StyledNavLink>
              )}
              {isWorkspacesOnHereAndNowEnabled && (
                <StyledNavLink
                  onClick={() => {
                    resetEntityState('workspaces');
                    sessionStorage.setItem(HaN_Previous_Tab, '/now/workspaces');
                  }}
                  $isActive={tab === 'workspaces'}
                  to={'/now/workspaces'}>
                  {t('hereAndNow:Workspaces')}
                </StyledNavLink>
              )}
              <StyledNavLinkWithIcon to={'/now/explore'}>
                <StyledIconNav
                  onClick={() => resetEntityState()}
                  weight="bold"
                  icon="search"
                  size="20px"
                />
              </StyledNavLinkWithIcon>
            </StyledTabs>
          </Div>
        )}

        {tab === 'explore' && !roomListVerticalOpen && (
          <FlexRow
            gridArea="tabs"
            display="none"
            sm={{display: 'flex'}}
            justifyContent="center"
            alignItems="center">
            <HaNExploreSearchBar
              exploreState={exploreState}
              onBack={handleOnBackExplore}
              showBackButton={!!(isWorkspacesOnHereAndNowEnabled || isMeetingCalendarEnabled)}
            />
          </FlexRow>
        )}

        {!showCalendarConsentMessageNoOccupancy ? (
          <Div
            gridArea="filter"
            zIndex={1}
            overflowX={'hidden'}
            className="min-h-11">
            <HaNFilters
              swiperFilterReset={resetListState}
              areFilterTransparent={areFiltersTransparent}
            />
          </Div>
        ) : null}

        {displayControlsRow ? (
          <ControlsRow
            alignItems="center"
            gridArea="controls"
            justifyContent="flex-end"
            gap={8}>
            {enabledFeatures.ImprovedHaN ? (
              <FloorSelector
                buildingId={buildingId}
                selectedFloorId={selectedFloorId}
                onFloorChange={(f) => setSelectedFloorId(f.id)}
              />
            ) : (
              <HaNControls floor={mapFloor?.number} />
            )}
          </ControlsRow>
        ) : null}
        <Div
          gridArea={
            showCalendarConsentMessageNoOccupancy
              ? 'controls / room-list-horizontal / room-list-horizontal / room-list-horizontal'
              : 'room-list-horizontal'
          }
          display="none"
          sm={{display: 'grid'}}>
          <HaNHorizontalListContent
            buildingId={buildingId}
            showCalendarConsentMessageNoOccupancy={showCalendarConsentMessageNoOccupancy}
            endDateTime={endDateTime}
            onClick={openObjectInformation}
            onSlideChange={handleOnSlideChange}
            rooms={rooms}
            startDateTime={startDateTime}
            exploreState={exploreState}
            ref={ref}
            areas={areas}
          />
        </Div>
        <RoomListGridArea
          gridArea="room-list"
          overflow="hidden"
          zIndex={2}
          height="calc(var(--100vh, 100vh) - 60px)"
          $roomListVerticalOpen={roomListVerticalOpen}>
          <Div
            position="relative"
            top="0"
            zIndex={1}
            left="0">
            {displayCalendarSyncTile && (
              <Div sm={{display: 'grid', gridArea: 'calendar-sync-tile', zIndex: 1}}>
                <HaNCalendarSyncTile />
              </Div>
            )}
            <Div
              sm={{display: 'none'}}
              zIndex={10}
              gridArea="title">
              <Title>
                <H3 as="h1">{buildingName}</H3>
                {allBuildings.length > 1 ? (
                  <IconButton
                    aria-label="building-select"
                    icon="caretDown"
                    iconButton="tertiary"
                    onClick={handleBuildingSelectClick}
                    size="small"
                  />
                ) : null}
              </Title>
            </Div>
            {tab !== 'explore' ? (
              <StyledTabs
                gridArea="tabs"
                zIndex={10}>
                {isMeetingCalendarEnabled || isWorkspacesOnHereAndNowEnabled ? (
                  <StyledNavLink
                    $isActive={tab === 'rooms'}
                    onClick={() => {
                      resetEntityState('rooms');
                      refreshRoomsData();
                      sessionStorage.setItem(HaN_Previous_Tab, '/now/rooms');
                    }}
                    to={'/now/rooms'}>
                    {t('hereAndNow:Rooms')}
                  </StyledNavLink>
                ) : null}
                {isWorkspacesOnHereAndNowEnabled ? (
                  <StyledNavLink
                    $isActive={tab === 'workspaces'}
                    onClick={() => {
                      resetEntityState('workspaces');
                      sessionStorage.setItem(HaN_Previous_Tab, '/now/workspaces');
                    }}
                    to={'/now/workspaces'}>
                    {t('hereAndNow:Workspaces')}
                  </StyledNavLink>
                ) : null}
                <StyledNavLinkWithIcon to={'/now/explore'}>
                  <StyledIconNav
                    onClick={() => resetEntityState()}
                    weight="bold"
                    icon="search"
                    size="20px"
                  />
                </StyledNavLinkWithIcon>
              </StyledTabs>
            ) : null}
          </Div>

          {tab === 'explore' ? (
            <HaNExploreTab
              exploreState={exploreState}
              onClick={openObjectInformation}
              handleOnBackExplore={handleOnBackExplore}
            />
          ) : (
            <>
              <Div
                display="none"
                sm={{display: 'block', padding: '16px 20px'}}>
                <HaNFilters
                  swiperFilterReset={resetListState}
                  areFilterTransparent={areFiltersTransparent}
                />
              </Div>
              <FlexCol
                sm={{display: !roomListVerticalOpen ? 'none' : 'flex'}}
                overflow="hidden"
                flex="auto"
                zIndex={2}>
                {showCalendarConsentMessageNoOccupancy && tab === 'rooms' ? (
                  <HaNCalendarSync />
                ) : (
                  <HaNVerticalListContent
                    endDateTime={endDateTime}
                    onClick={openObjectInformation}
                    onHover={setHoverRoomId}
                    rooms={rooms}
                    areas={areas}
                    exploreState={exploreState}
                    selectedObject={selectedObject}
                    startDateTime={startDateTime}
                  />
                )}
              </FlexCol>
            </>
          )}
        </RoomListGridArea>
      </HereAndNowLayout>

      <HaNModalContainer
        setWarnBeforeOpeningNewRoom={setWarnBeforeOpeningNewRoom}
        onClose={closeDetailsModal}
        entity={selectedObject}
        show={roomInformationOpen}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        duration={duration}
        onBookNowCallback={handleBookNow}
      />
    </>
  );
};
